import React from "react";
import { BarChart3, Target, Activity, ShieldCheck, Sparkles } from "lucide-react";
import { FootyStatsMetrics } from "@/types/api";

// ---- data-safe formatters -------------------------------------------------
const fin = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const pct = (v: number) => `%${Math.round(v)}`;
const dec = (v: number) => v.toFixed(2);

type Lang = "tr" | "en";
const t = (lang: Lang, tr: string, en: string) => (lang === "tr" ? tr : en);

interface Cell { label: string; value: string }

// Build the list of cells that actually have finite values (no fabricated 0s).
export function compactCells(fs: FootyStatsMetrics, code: string, lang: Lang): Cell[] {
  const c = (code || "").toUpperCase();
  const out: Cell[] = [];
  const push = (ok: boolean, label: string, value: string) => { if (ok) out.push({ label, value }); };

  if (c.includes("UNDER_3_5") || c.includes("UNDER35") || c.includes("U3_5")) {
    push(fin(fs.under_35_pct), t(lang, "3.5 Alt Pot.", "Under 3.5"), fin(fs.under_35_pct) ? pct(fs.under_35_pct!) : "");
    push(fin(fs.total_xg), t(lang, "Toplam xG", "Total xG"), fin(fs.total_xg) ? dec(fs.total_xg!) : "");
    push(fin(fs.btts_pct), "BTTS", fin(fs.btts_pct) ? pct(fs.btts_pct!) : "");
    push(fin(fs.avg_goals_potential), t(lang, "Gol Ort.", "Avg Goals"), fin(fs.avg_goals_potential) ? dec(fs.avg_goals_potential!) : "");
  } else if (c.includes("DOUBLE_CHANCE") || c.includes("DC_X2") || c.endsWith("_X2") || c.includes("X2")) {
    push(fin(fs.home_ppg), t(lang, "Ev PPG", "Home PPG"), fin(fs.home_ppg) ? dec(fs.home_ppg!) : "");
    push(fin(fs.away_ppg), t(lang, "Dep PPG", "Away PPG"), fin(fs.away_ppg) ? dec(fs.away_ppg!) : "");
    push(fin(fs.home_xg), t(lang, "Ev xG", "Home xG"), fin(fs.home_xg) ? dec(fs.home_xg!) : "");
    push(fin(fs.away_xg), t(lang, "Dep xG", "Away xG"), fin(fs.away_xg) ? dec(fs.away_xg!) : "");
  } else {
    push(fin(fs.total_xg), t(lang, "Toplam xG", "Total xG"), fin(fs.total_xg) ? dec(fs.total_xg!) : "");
    push(fin(fs.btts_pct), "BTTS", fin(fs.btts_pct) ? pct(fs.btts_pct!) : "");
    push(fin(fs.over_25_pct), t(lang, "2.5 Üst", "Over 2.5"), fin(fs.over_25_pct) ? pct(fs.over_25_pct!) : "");
    push(fin(fs.avg_goals_potential), t(lang, "Gol Ort.", "Avg Goals"), fin(fs.avg_goals_potential) ? dec(fs.avg_goals_potential!) : "");
  }
  return out.slice(0, 4);
}

function StatPill({ label, value }: Cell) {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.03] px-2.5 py-1.5" data-testid="fs-stat-pill">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{label}</p>
      <p className="num text-sm font-bold text-foreground/95">{value}</p>
    </div>
  );
}

// Big summary tile for the dedicated prematch detail "Maç Özeti".
export function SummaryTile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-atlas/25 bg-atlas/[0.06]" : "border-white/8 bg-white/[0.03]"}`} data-testid="fs-summary-tile">
      <p className="num text-xl font-extrabold text-foreground">{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

// Horizontal percentage meter (0–100). Only render when the value is finite.
export function PctBar({ label, value }: { label: string; value: number }) {
  const w = Math.max(0, Math.min(100, value));
  return (
    <div data-testid="fs-pct-bar">
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="num font-bold text-foreground/90">{pct(value)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
        <div className="h-full rounded-full bg-gradient-to-r from-atlas-500 to-atlas-300" style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

// Home vs Away comparison row with proportional (neutral) bars. Never implies
// which side is "better" — bars are split by share of the combined value.
export function CompareRow({ label, home, away }: { label: string; home: number | null | undefined; away: number | null | undefined }) {
  const h = fin(home) ? (home as number) : null;
  const a = fin(away) ? (away as number) : null;
  if (h == null && a == null) return null;
  const sum = (h ?? 0) + (a ?? 0);
  const hw = sum > 0 ? ((h ?? 0) / sum) * 100 : 50;
  const aw = sum > 0 ? ((a ?? 0) / sum) * 100 : 50;
  return (
    <div className="grid grid-cols-[2.6rem_1fr_2.6rem] items-center gap-2" data-testid="fs-compare-row">
      <span className="num text-left text-xs font-bold text-foreground/90">{h != null ? dec(h) : "—"}</span>
      <div>
        <p className="mb-1 text-center text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className="flex h-1.5 items-center gap-0.5">
          <div className="flex h-full flex-1 justify-end overflow-hidden rounded-l-full bg-white/8"><div className="h-full rounded-l-full bg-atlas-400/80" style={{ width: `${hw}%` }} /></div>
          <div className="flex h-full flex-1 overflow-hidden rounded-r-full bg-white/8"><div className="h-full rounded-r-full bg-gold-400/70" style={{ width: `${aw}%` }} /></div>
        </div>
      </div>
      <span className="num text-right text-xs font-bold text-foreground/90">{a != null ? dec(a) : "—"}</span>
    </div>
  );
}

// ---- compact "Maç Profili" strip (collapsed card) -------------------------
export function MatchProfileStrip({ fs, marketCode, lang }: { fs: FootyStatsMetrics; marketCode: string; lang: Lang }) {
  const cells = compactCells(fs, marketCode, lang);
  if (cells.length === 0) return null;
  return (
    <div className="mt-3" data-testid="fs-profile-strip">
      <p className="eyebrow mb-1.5 flex items-center gap-1.5 text-muted-foreground"><BarChart3 className="h-3 w-3 text-atlas-400" /> {t(lang, "Maç Profili", "Match Profile")}</p>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {cells.map((c, i) => <StatPill key={i} {...c} />)}
      </div>
    </div>
  );
}

// ---- form chips -----------------------------------------------------------
export function FormChips({ form }: { form: string }) {
  const letters = (form.toUpperCase().match(/[WDL]/g) || []).slice(-5);
  if (letters.length === 0) return <span className="num text-xs font-semibold text-foreground/80">{form}</span>;
  const cls: Record<string, string> = {
    W: "bg-emerald-500/20 text-emerald-300",
    D: "bg-white/10 text-muted-foreground",
    L: "bg-rose-500/20 text-rose-300",
  };
  return (
    <div className="flex items-center gap-1">
      {letters.map((l, i) => <span key={i} className={`inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold ${cls[l]}`}>{l}</span>)}
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-1.5 flex items-center gap-1.5 text-atlas-300">{icon} {title}</p>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">{children}</div>
    </div>
  );
}

// ---- expanded detail panel ------------------------------------------------
export function MatchProfileDetail({ fs, lang }: { fs: FootyStatsMetrics; lang: Lang }) {
  const compareRows: { label: string; home?: number | null; away?: number | null }[] = [
    { label: "PPG", home: fs.home_ppg, away: fs.away_ppg },
    { label: "xG", home: fs.home_xg, away: fs.away_xg },
    { label: "xGA", home: fs.home_xga, away: fs.away_xga },
  ].filter((r) => fin(r.home) || fin(r.away));
  const goalBars: { label: string; value: number }[] = [
    { label: t(lang, "1.5 Üst", "Over 1.5"), value: fs.over_15_pct as number },
    { label: t(lang, "2.5 Üst", "Over 2.5"), value: fs.over_25_pct as number },
    { label: t(lang, "3.5 Üst", "Over 3.5"), value: fs.over_35_pct as number },
    { label: t(lang, "3.5 Alt", "Under 3.5"), value: fs.under_35_pct as number },
    { label: "BTTS", value: fs.btts_pct as number },
  ].filter((b) => fin(b.value));
  const cell = (ok: boolean, label: string, value: string) => (ok ? <StatPill label={label} value={value} /> : null);
  const dynamics = [
    cell(fin(fs.corners_avg), t(lang, "Korner Ort.", "Corners Avg"), fin(fs.corners_avg) ? dec(fs.corners_avg!) : ""),
    cell(fin(fs.cards_avg), t(lang, "Kart Ort.", "Cards Avg"), fin(fs.cards_avg) ? dec(fs.cards_avg!) : ""),
    cell(fin(fs.avg_goals_potential), t(lang, "Gol Ort.", "Avg Goals"), fin(fs.avg_goals_potential) ? dec(fs.avg_goals_potential!) : ""),
  ].filter(Boolean);
  const hasForm = (typeof fs.home_form === "string" && fs.home_form.trim()) || (typeof fs.away_form === "string" && fs.away_form.trim());
  const h2h = fs.h2h_present === true;
  const trends = fs.trends_present === true;

  const anything = compareRows.length || goalBars.length || dynamics.length || hasForm || h2h || trends;
  if (!anything) return null;

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="fs-profile-detail">
      {compareRows.length > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5 text-atlas-300"><Activity className="h-3 w-3" /> {t(lang, "ATLAS Veri Profili", "ATLAS Data Profile")}</p>
          <div className="space-y-2">{compareRows.map((r) => <CompareRow key={r.label} label={r.label} home={r.home} away={r.away} />)}</div>
        </div>
      )}
      {goalBars.length > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5 text-atlas-300"><Target className="h-3 w-3" /> {t(lang, "Gol Profili", "Goal Profile")}</p>
          <div className="space-y-2">{goalBars.map((b) => <PctBar key={b.label} label={b.label} value={b.value} />)}</div>
        </div>
      )}
      {dynamics.length > 0 && <Section icon={<BarChart3 className="h-3 w-3" />} title={t(lang, "Maç Dinamiği", "Match Dynamics")}>{dynamics}</Section>}
      {hasForm && (
        <div>
          <p className="eyebrow mb-1.5 flex items-center gap-1.5 text-atlas-300"><ShieldCheck className="h-3 w-3" /> {t(lang, "Form", "Form")}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            {typeof fs.home_form === "string" && fs.home_form.trim() && <span className="flex items-center gap-1.5">{t(lang, "Ev", "Home")} <FormChips form={fs.home_form} /></span>}
            {typeof fs.away_form === "string" && fs.away_form.trim() && <span className="flex items-center gap-1.5">{t(lang, "Dep", "Away")} <FormChips form={fs.away_form} /></span>}
          </div>
        </div>
      )}
      {(h2h || trends) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {h2h && <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold text-foreground/70"><Sparkles className="h-2.5 w-2.5 text-atlas-400" /> {t(lang, "H2H verisi mevcut", "H2H data available")}</span>}
          {trends && <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold text-foreground/70"><Sparkles className="h-2.5 w-2.5 text-atlas-400" /> {t(lang, "Trend verisi mevcut", "Trend data available")}</span>}
        </div>
      )}
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground/60">{t(lang, "Veri: FootyStats", "Data: FootyStats")}</p>
    </div>
  );
}
